/**
 * Infusion Orders List Page — Issue #345.
 *
 * Wraps the existing useInfusionOrdersList + the new
 * useInfusionOrderTransitionById hook in the shared AdminDataTable so the
 * page picks up advanced search, column visibility, loading/empty/error
 * states and a permission-aware row action menu. Medical-safety red lines
 * are preserved verbatim: identity verification is mandatory before infusion
 * and is enforced by the backend; the list-level row action only exposes the
 * `cancelled` / `aborted` non-physical removal semantics that the backend
 * already supports. Cancel/abort require an audit-grade reason that we pass
 * through as `abortReason` per the OpenAPI contract.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Ban, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  useInfusionOrdersList,
  useInfusionOrderTransitionById,
  type InfusionOrderView,
} from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { canReadInfusion, canUpdateInfusion } from '@/lib/labPermissions';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

const INFUSION_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'scheduled', label: '已排期' },
  { value: 'identity_check_pending', label: '待身份核对' },
  { value: 'ready_for_infusion', label: '待输液' },
  { value: 'infusing', label: '输液中' },
  { value: 'completed', label: '已完成' },
  { value: 'aborted', label: '已中止' },
  { value: 'cancelled', label: '已取消' },
  { value: 'identity_check_failed', label: '核对失败' },
];

function getStatusLabel(status: string): string {
  return (
    INFUSION_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-900',
    identity_check_pending: 'bg-yellow-100 text-yellow-900',
    ready_for_infusion: 'bg-green-100 text-green-900',
    infusing: 'bg-purple-100 text-purple-900',
    completed: 'bg-green-100 text-green-900',
    aborted: 'bg-gray-100 text-gray-900',
    cancelled: 'bg-gray-100 text-gray-900',
    identity_check_failed: 'bg-red-100 text-red-900',
  };
  return colors[status] || 'bg-gray-100 text-gray-900';
}

export default function InfusionOrdersListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch, error } = useInfusionOrdersList();
  const { transition, loading: transitioning } =
    useInfusionOrderTransitionById();

  const [orders, setOrders] = useState<InfusionOrderView[]>([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    customerId: '',
    status: '',
  });

  const limit = 20;
  const statusFilter = searchValues['status'] ?? '';
  const customerIdFilter = (searchValues['customerId'] ?? '').trim();

  const canRead = canReadInfusion(permissions);
  const canUpdate = canUpdateInfusion(permissions);

  const refresh = useCallback(async () => {
    if (!canRead) return;
    setPageLoading(true);
    try {
      const data = await fetch({
        page,
        limit,
        ...(statusFilter && { status: statusFilter }),
        ...(customerIdFilter && { customerId: customerIdFilter }),
      });
      if (data) setOrders(data.items || []);
    } catch {
      toast.error(`加载输液单失败：${error?.message ?? '未知错误'}`);
    } finally {
      setPageLoading(false);
    }
  }, [canRead, customerIdFilter, error?.message, fetch, page, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const columns: ReadonlyArray<AdminDataTableColumn<InfusionOrderView>> = useMemo(
    () => [
      {
        key: 'id',
        header: '输液单 ID',
        alwaysVisible: true,
        render: (o) => <span className="font-mono text-sm">{o.id}</span>,
      },
      {
        key: 'cellBatchId',
        header: '批次 ID',
        render: (o) => <span className="font-mono text-sm">{o.cellBatchId}</span>,
      },
      {
        key: 'customerId',
        header: '客户 ID',
        render: (o) => <span className="font-mono text-sm">{o.customerId}</span>,
      },
      {
        key: 'status',
        header: '状态',
        render: (o) => (
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(o.status)}`}
          >
            {getStatusLabel(o.status)}
          </span>
        ),
      },
      {
        key: 'version',
        header: '版本',
        defaultHidden: true,
        render: (o) => `v${o.version}`,
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (o) => new Date(o.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<InfusionOrderView>> =
    useMemo(
      () => [
        {
          key: 'view',
          label: '查看详情',
          icon: <Eye className="h-3.5 w-3.5" />,
          onSelect: (o) => navigate(`/lab/infusion/${o.id}`),
        },
        {
          key: 'edit',
          label: '更新状态',
          icon: <Pencil className="h-3.5 w-3.5" />,
          visible: () => canUpdate,
          onSelect: (o) => navigate(`/lab/infusion/${o.id}`),
        },
        {
          key: 'cancel',
          // 输液单不可物理删除。可在 infusing 之前的状态触发 cancel；已开始
          // 输液的单需要走 abort（带原因），由后端状态机校验。
          label: '取消（标记 cancelled / aborted）',
          icon: <Ban className="h-3.5 w-3.5" />,
          destructive: true,
          visible: (o) =>
            canUpdate &&
            o.status !== 'cancelled' &&
            o.status !== 'aborted' &&
            o.status !== 'completed',
          disabled: () => transitioning,
          onSelect: async (o) => {
            const reason = window.prompt(
              `请说明${o.status === 'infusing' ? '中止' : '取消'}原因（必填，将写入审计日志）`,
            );
            const trimmed = reason?.trim();
            if (!trimmed) return;
            const ok = await transition(o.id, {
              nextStatus: o.status === 'infusing' ? 'aborted' : 'cancelled',
              expectedVersion: o.version,
              abortReason: trimmed,
            });
            if (ok) {
              toast.success('输液单状态已更新');
              await refresh();
            }
          },
        },
      ],
      [canUpdate, navigate, refresh, transition, transitioning],
    );

  if (!canRead) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看输液单的权限</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">输液单管理</h1>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </div>

      <Card className="border-red-200 bg-red-50 p-4">
        <p className="text-sm font-semibold text-red-900">⚠️ 医疗安全红线</p>
        <p className="mt-2 text-sm text-red-800">
          输液前必须执行身份核对。执行人与审核人必须不同。核对失败将自动创建异常事件
          并阻止后续操作。本页提供的「取消 / 中止」操作只调用后端状态机，不会物理
          删除任何回输历史。
        </p>
      </Card>

      <AdminDataTable<InfusionOrderView>
        testIdPrefix="infusion-orders"
        columns={columns}
        rows={orders}
        isLoading={pageLoading}
        emptyMessage="暂无输液单数据"
        filters={{
          fields: [
            {
              kind: 'text',
              name: 'customerId',
              label: '客户 ID',
              placeholder: '按客户 ID 精确搜索',
            },
            {
              kind: 'select',
              name: 'status',
              label: '状态',
              options: INFUSION_STATUS_OPTIONS.map(({ value, label }) => ({
                value,
                label,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ customerId: '', status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total:
            orders.length === limit
              ? page * limit + 1
              : (page - 1) * limit + orders.length,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
